import { useInputControl, type FieldMetadata } from '@conform-to/react'
// eslint-disable-next-line @typescript-eslint/naming-convention
import Select, {
  components,
  OptionProps,
  PlaceholderProps,
  StylesConfig,
} from 'react-select'
import { z } from 'zod'

import { Checkbox, IconHuman } from '~/components'

export const numberOfUsersMultiSelectFormSchema = z.object({
  minPeople: z.coerce.number().int().min(1).optional(),
})

export const partialNumberOfUsersMultiSelectFormSchema =
  numberOfUsersMultiSelectFormSchema.partial()

type NumberOfUsersType = z.infer<
  typeof numberOfUsersMultiSelectFormSchema
>['minPeople']

type Option = {
  value: string
  label: string
}

const options: Option[] = [
  { value: '1', label: '1名以上' },
  { value: '2', label: '2名以上' },
  { value: '3', label: '3名以上' },
  { value: '4', label: '4名以上' },
  { value: '5', label: '5名以上' },
  { value: '6', label: '6名以上' },
  { value: '7', label: '7名以上' },
  { value: '8', label: '8名以上' },
  { value: '9', label: '9名以上' },
  { value: '10', label: '10名以上' },
  { value: '20', label: '20名以上' },
  { value: '30', label: '30名以上' },
]

const customStyles: StylesConfig<Option> = {
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? 'transparent'
      : provided.backgroundColor,
    color: state.isSelected ? '#000' : provided.color,
  }),
  control: (provided, state) => ({
    ...provided,
    fontSize: 16,
    borderColor: state.isFocused ? '#09909E' : provided.borderColor,
    boxShadow: state.isFocused ? '0 0 0 2px #09909E' : provided.boxShadow,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '&:hover': {
      borderColor: state.isFocused
        ? '#09909E'
        : provided[':hover']?.borderColor,
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '1rem',
    flexWrap: 'nowrap',
    overflowX: 'auto',
  }),
  // NOTE:MultiSelectコンポーネントのプレースホルダーにあたっているスタイルと同等のスタイルを設定
  singleValue: (provided) => ({
    ...provided,
    fontSize: '85%',
    backgroundColor: '#E6E5E5',
    padding: '3px 6px',
    width: 'fit-content',
    borderRadius: '2px',
    boxSizing: 'border-box',
  }),
}

const OptionComponent = (props: OptionProps<Option>) => {
  return (
    <components.Option {...props}>
      <Checkbox
        checked={props.isSelected}
        label={props.label}
        textSize="base"
      />
    </components.Option>
  )
}

const CustomPlaceholder = (
  props: PlaceholderProps<Option>,
  placeholderIcon?: React.ReactNode,
) => {
  return (
    <components.Placeholder {...props}>
      <div className="flex items-center">
        {placeholderIcon && (
          <span className="mr-2 h-4 w-4">{placeholderIcon}</span>
        )}
        <span className="pt-1">人数を選択</span>
      </div>
    </components.Placeholder>
  )
}

export const useNumberOfUsersForm = (
  meta: FieldMetadata<NumberOfUsersType>,
  changeWatchedMinPeople?: (minPeople: string) => void,
) => {
  const control = useInputControl({
    ...meta,
    initialValue: meta.initialValue ?? '',
  })

  const onChange: React.ComponentProps<
    typeof Select<Option, false>
  >['onChange'] = (newValue) => {
    const value = newValue?.value || ''
    changeWatchedMinPeople?.(value)
    control.change(typeof value === 'string' ? value : '')
  }

  return { value: control.value || '', onChange, name: meta.name }
}

type Props = {
  ariaLabel?: string
  value: string
  onChange: React.ComponentProps<typeof Select<Option, false>>['onChange']
  name: string
}

export const NumberOfUsersMultiSelectForm = ({
  name,
  value,
  onChange,
}: Props) => {
  return (
    <Select<Option, false>
      components={{
        // eslint-disable-next-line @typescript-eslint/naming-convention
        Option: (props) => OptionComponent(props),
        // eslint-disable-next-line @typescript-eslint/naming-convention
        Placeholder: (props) => CustomPlaceholder(props, <IconHuman />),
      }}
      isClearable
      name={name}
      onChange={onChange}
      options={options}
      styles={customStyles}
      value={value ? options.find((option) => option.value === value) : null}
    />
  )
}
